import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { MapPin, Calendar, User, ArrowLeft, Repeat, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatDate, getConditionColor } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ListingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token, isAuthenticated } = useAuth();
  
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Trade request modal
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [myListings, setMyListings] = useState([]);
  const [selectedListingId, setSelectedListingId] = useState('');
  const [tradeMessage, setTradeMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchListing();
  }, [id]);

  const fetchListing = async () => {
    try {
      const response = await axios.get(`${API}/listings/${id}`);
      setListing(response.data);
    } catch (error) {
      console.error('Error fetching listing:', error);
      toast.error('Listing not found');
      navigate('/browse');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyListings = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/listings/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter only active listings
      setMyListings(response.data.filter(l => l.status === 'active'));
    } catch (error) {
      console.error('Error fetching my listings:', error);
    }
  };

  const handleRequestTrade = async () => {
    if (!isAuthenticated) {
      toast.error('Please log in to request a trade');
      navigate('/login');
      return;
    }
    
    await fetchMyListings();
    setShowTradeModal(true);
  };

  const submitTradeRequest = async () => {
    if (!selectedListingId) {
      toast.error('Please select an item to offer');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/trade-requests`,
        {
          receiver_listing_id: listing.id,
          sender_listing_id: selectedListingId,
          message: tradeMessage || undefined
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Trade request sent!');
      setShowTradeModal(false);
      setSelectedListingId('');
      setTradeMessage('');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send trade request';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const nextImage = () => {
    if (listing?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % listing.images.length);
    }
  };

  const prevImage = () => {
    if (listing?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + listing.images.length) % listing.images.length);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!listing) return null;

  const isOwner = user?.id === listing.user_id;

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#F9F9F7] animate-fadeIn">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-stone-600 hover:text-stone-900 mb-6 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to listings
        </button>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Images */}
          <div className="space-y-4">
            <div className="relative aspect-square rounded-2xl overflow-hidden bg-white border border-stone-100">
              {listing.images?.[0] ? (
                <>
                  <img
                    src={listing.images[currentImageIndex]}
                    alt={listing.title}
                    className="w-full h-full object-cover"
                  />
                  {listing.images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 backdrop-blur flex items-center justify-center hover:bg-white transition-colors"
                        data-testid="prev-image-btn"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 backdrop-blur flex items-center justify-center hover:bg-white transition-colors"
                        data-testid="next-image-btn"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
                        {listing.images.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => setCurrentImageIndex(idx)}
                            className={`w-2 h-2 rounded-full transition-colors ${
                              idx === currentImageIndex ? 'bg-white' : 'bg-white/50'
                            }`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-stone-400">
                  No Image Available
                </div>
              )}
            </div>

            {/* Thumbnail gallery */}
            {listing.images?.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {listing.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-colors ${
                      idx === currentImageIndex ? 'border-[#008080]' : 'border-transparent'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="space-y-6">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getConditionColor(listing.condition)}`}>
                  {listing.condition}
                </span>
                <span className="px-3 py-1 rounded-full bg-stone-100 text-stone-600 text-sm">
                  {listing.category}
                </span>
              </div>
              <h1 className="text-3xl font-bold text-stone-900 mb-4" data-testid="listing-title">
                {listing.title}
              </h1>
              <p className="text-stone-600 leading-relaxed" data-testid="listing-description">
                {listing.description}
              </p>
            </div>

            {/* Meta info */}
            <div className="flex flex-wrap gap-4 text-sm text-stone-500">
              <div className="flex items-center">
                <MapPin className="w-4 h-4 mr-1.5" />
                {listing.location}
              </div>
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-1.5" />
                Listed {formatDate(listing.created_at)}
              </div>
            </div>

            {/* Owner info */}
            <div className="p-4 bg-white rounded-xl border border-stone-100">
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-full bg-[#008080] text-white flex items-center justify-center text-lg font-medium mr-4">
                  {listing.user_name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-medium text-stone-900">{listing.user_name}</p>
                  <p className="text-sm text-stone-500 flex items-center">
                    <MapPin className="w-3 h-3 mr-1" />
                    {listing.user_location}
                  </p>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            {!isOwner && listing.status === 'active' && (
              <Button
                onClick={handleRequestTrade}
                className="w-full h-14 bg-[#008080] hover:bg-[#008080]/90 rounded-full text-lg font-medium"
                data-testid="request-trade-btn"
              >
                <Repeat className="w-5 h-5 mr-2" />
                Request Trade
              </Button>
            )}

            {isOwner && (
              <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
                <p className="text-amber-800 text-sm">
                  This is your listing. You can manage it from your{' '}
                  <Link to="/dashboard" className="font-medium underline">dashboard</Link>.
                </p>
              </div>
            )}

            {listing.status === 'traded' && (
              <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-100">
                <p className="text-indigo-800 text-sm font-medium">
                  This item has been traded.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Trade Request Modal */}
      <Dialog open={showTradeModal} onOpenChange={setShowTradeModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Request a Trade</DialogTitle>
            <DialogDescription>
              Select one of your items to offer in exchange for "{listing?.title}"
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {myListings.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-stone-600 mb-4">You don't have any items listed yet.</p>
                <Link to="/create-listing">
                  <Button className="bg-[#008080] hover:bg-[#008080]/90 rounded-full">
                    List an Item
                  </Button>
                </Link>
              </div>
            ) : (
              <>
                <div>
                  <label className="text-sm font-medium text-stone-700 mb-1.5 block">
                    Your Item to Offer
                  </label>
                  <Select
                    value={selectedListingId}
                    onValueChange={setSelectedListingId}
                  >
                    <SelectTrigger className="h-12 rounded-xl" data-testid="select-my-item">
                      <SelectValue placeholder="Select an item" />
                    </SelectTrigger>
                    <SelectContent>
                      {myListings.map((item) => (
                        <SelectItem key={item.id} value={item.id}>
                          {item.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-stone-700 mb-1.5 block">
                    Message (optional)
                  </label>
                  <Textarea
                    placeholder="Add a message to the owner..."
                    value={tradeMessage}
                    onChange={(e) => setTradeMessage(e.target.value)}
                    className="rounded-xl min-h-[100px]"
                    data-testid="trade-message-input"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setShowTradeModal(false)}
                    className="flex-1 h-12 rounded-full"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={submitTradeRequest}
                    disabled={!selectedListingId || submitting}
                    className="flex-1 h-12 bg-[#008080] hover:bg-[#008080]/90 rounded-full"
                    data-testid="submit-trade-request-btn"
                  >
                    {submitting ? 'Sending...' : 'Send Request'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
